import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  FiAward, FiGift, FiShoppingBag, FiStar, 
  FiCheckCircle, FiArrowRight, FiClock, 
  FiTrendingUp, FiX, FiZap
} from 'react-icons/fi';


const Wallet = () => {
  const [currentPoints, setCurrentPoints] = useState(250);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('earn');

  // Sample data
  const waysToEarn = [
    { id: 1, title: "Complete Profile", points: 100, completed: true, icon: <FiCheckCircle /> },
    { id: 2, title: "Daily Check-in", points: 50, completed: false, icon: <FiClock /> },
    { id: 3, title: "Refer a Friend", points: 200, completed: false, icon: <FiShoppingBag /> },
    { id: 4, title: "Make a Purchase", points: 10, completed: false, icon: <FiShoppingBag /> },
    { id: 5, title: "Write a Review", points: 75, completed: false, icon: <FiStar /> },
  ];

  const rewardProducts = [
    { id: 1, name: "Premium Subscription", points: 500, image: "https://source.unsplash.com/random/300x200?premium" },
    { id: 2, name: "Discount Coupon 10%", points: 300, image: "https://source.unsplash.com/random/300x200?coupon" },
    { id: 3, name: "Exclusive E-book", points: 700, image: "https://source.unsplash.com/random/300x200?ebook" },
    { id: 4, name: "Free Shipping", points: 200, image: "https://source.unsplash.com/random/300x200?shipping" },
    { id: 5, name: "VIP Badge", points: 1000, image: "https://source.unsplash.com/random/300x200?vip" },
  ];

  const nextLevelPoints = 2000;
  const progressPercentage = Math.min((currentPoints / nextLevelPoints) * 100, 100);

  const handleRedeem = (product) => {
    if (currentPoints >= product.points) {
      setSelectedProduct(product);
      setShowRedeemModal(true);
    }
  };

  const confirmRedeem = () => {
    setCurrentPoints(prev => prev - selectedProduct.points);
    setShowRedeemModal(false);
  };

  return (
    <div className="rewards-container">
      {/* Header Section */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rewards-header"
      >
        <div className="header-content">
          <div className="title-group">
            <FiAward className="title-icon" />
            <h1>My Rewards</h1>
          </div>
          <p className="subtitle">Earn points and unlock exclusive benefits</p>
        </div>

        <div className="points-display">
          <div className="points-content">
            <span className="points-label">Your Points</span>
            <div className="points-value">
              <CountUp 
                end={currentPoints} 
                duration={1.5}
                separator=","
              />
              <FiStar className="star-icon" />
            </div>
          </div>
        </div>
      </motion.header>

      {/* Progress Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="progress-section"
      >
        <div className="progress-labels">
          <span>Explorer</span>
          <span>Adventurer</span>
        </div>
        <div className="progress-track">
          <motion.div 
            className="progress-bar"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1, delay: 0.3 }}
          />
          <div className="progress-thumb" style={{ left: `${progressPercentage}%` }} />
        </div>
        <div className="progress-points">
          <span>{currentPoints} pts</span>
          <span>{nextLevelPoints - currentPoints} to next level</span>
        </div>
      </motion.section>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="tabs-container"
      >
        <button 
          className={`tab ${activeTab === 'earn' ? 'active' : ''}`}
          onClick={() => setActiveTab('earn')}
        >
          <FiTrendingUp className="tab-icon" />
          Earn Points
        </button>
        <button 
          className={`tab ${activeTab === 'redeem' ? 'active' : ''}`}
          onClick={() => setActiveTab('redeem')}
        >
          <FiGift className="tab-icon" />
          Redeem Rewards
        </button>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: activeTab === 'earn' ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: activeTab === 'earn' ? -20 : 20 }}
          transition={{ duration: 0.3 }}
          className="tab-content"
        >
          {activeTab === 'earn' ? (
            <div className="earn-points">
              <h2 className="section-title">
                <FiTrendingUp className="section-icon" />
                Ways to Earn Points
              </h2>
              <div className="tasks-grid">
                {waysToEarn.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`task-card ${item.completed ? 'completed' : ''}`}
                  >
                    <div className="task-icon">{item.icon}</div>
                    <div className="task-info">
                      <h3>{item.title}</h3>
                      <p>+{item.points} points</p>
                    </div>
                    <div className={`task-status ${item.completed ? 'completed' : 'pending'}`}>
                      {item.completed ? 'Completed' : 'Pending'}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div className="redeem-rewards">
              <h2 className="section-title">
                <FiGift className="section-icon" />
                Available Rewards
              </h2>
              <div className="rewards-grid">
                {rewardProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.98 }}
                    className={`reward-card ${currentPoints >= product.points ? 'available' : 'locked'}`}
                  >
                    <div 
                      className="reward-image"
                      style={{ backgroundImage: `url(${product.image})` }}
                    >
                      {currentPoints < product.points && (
                        <div className="locked-overlay">
                          <FiZap className="lock-icon" />
                          <span>Need {product.points - currentPoints} more pts</span>
                        </div>
                      )}
                    </div>
                    <div className="reward-details">
                      <h3>{product.name}</h3>
                      <div className="reward-footer">
                        <span className="reward-points">{product.points} pts</span>
                        <button
                          className={`redeem-button ${currentPoints >= product.points ? '' : 'disabled'}`}
                          onClick={() => handleRedeem(product)}
                          disabled={currentPoints < product.points}
                        >
                          Redeem <FiArrowRight />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Redeem Modal */}
      <AnimatePresence>
        {showRedeemModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRedeemModal(false)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setShowRedeemModal(false)}>
                <FiX />
              </button>
              
              <div className="modal-icon">
                <FiGift />
              </div>
              
              <h2>Confirm Redemption</h2>
              <p className="modal-product">{selectedProduct?.name}</p>
              
              <div className="modal-points">
                <div className="points-row">
                  <span>Current Points:</span>
                  <span className="points-value">{currentPoints}</span>
                </div>
                <div className="points-row">
                  <span>Cost:</span>
                  <span className="points-value">-{selectedProduct?.points}</span>
                </div>
                <div className="points-divider" />
                <div className="points-row total">
                  <span>Remaining:</span>
                  <span className="points-value">
                    {currentPoints - (selectedProduct?.points || 0)}
                  </span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button className="cancel-button" onClick={() => setShowRedeemModal(false)}>
                  Cancel
                </button>
                <button className="confirm-button" onClick={confirmRedeem}>
                  Confirm Redemption
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Wallet;